
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface SocialMediaLinks {
  facebook?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
}

export interface BrandColors {
  primary: string;
  secondary: string;
  accent: string;
}

export interface CompanyInfo {
  companyName: string;
  contactPerson: string;
  email: string;
  phone?: string;
  website?: string;
  address: string;
  logo?: string;
  logoUrl?: string;
  finmaNr?: string;
  socialMedia?: SocialMediaLinks;
  brandColors?: BrandColors;
}

interface CompanySettingsContextType {
  companyInfo: CompanyInfo | null;
  updateCompanyInfo: (info: CompanyInfo) => void;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  isLoading: boolean;
}

// Define a specific type for our database response
interface CompanySettingsRow {
  id: string;
  user_id: string;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  address: string | null;
  logo_url: string | null;
  finma_nr: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  accent_color: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  youtube_url: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const defaultCompanyInfo: CompanyInfo = {
  companyName: '',
  contactPerson: '',
  email: '',
  address: '',
  logoUrl: '',
  finmaNr: '',
  brandColors: {
    primary: '#18B08D',
    secondary: '#1A1F2C',
    accent: '#7E69AB',
  },
  socialMedia: {
    facebook: '',
    instagram: '',
    linkedin: '',
    youtube: '',
  }
};

const CompanySettingsContext = createContext<CompanySettingsContextType>({
  companyInfo: defaultCompanyInfo,
  updateCompanyInfo: () => {},
  isModalOpen: false,
  setIsModalOpen: () => {},
  isLoading: false,
});

export const CompanySettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const loadCompanyInfo = async () => {
      if (!user) {
        setCompanyInfo(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Using the generic query approach with explicit typing
        const { data, error } = await supabase
          .from('company_settings')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle<CompanySettingsRow>();

        if (error) {
          throw error;
        }

        if (data) {
          const info: CompanyInfo = {
            companyName: data.company_name || '',
            contactPerson: data.contact_person || '',
            email: data.email || '',
            phone: data.phone || '',
            website: data.website || '',
            address: data.address || '',
            logoUrl: data.logo_url || '',
            finmaNr: data.finma_nr || '',
            brandColors: {
              primary: data.primary_color || '#18B08D',
              secondary: data.secondary_color || '#1A1F2C',
              accent: data.accent_color || '#7E69AB',
            },
            socialMedia: {
              facebook: data.facebook_url || '',
              instagram: data.instagram_url || '',
              linkedin: data.linkedin_url || '',
              youtube: data.youtube_url || '',
            },
          };

          setCompanyInfo(info);

          // Store in local storage for other components
          localStorage.setItem('companyInfo', JSON.stringify(info));
          console.log('Loaded company settings from database');
        } else {
          // No data found - set to null
          setCompanyInfo(null);
          localStorage.removeItem('companyInfo');
        }
      } catch (error) {
        console.error('Error loading company settings:', error);
        toast({
          title: 'Fehler',
          description: 'Fehler beim Laden der Unternehmensdaten.',
          variant: 'destructive',
        });
        
        // Try to load from localStorage as fallback
        const localData = localStorage.getItem('companyInfo');
        if (localData) {
          try {
            setCompanyInfo(JSON.parse(localData));
          } catch (e) {
            console.error('Error parsing local company data:', e);
            localStorage.removeItem('companyInfo');
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadCompanyInfo();
  }, [user, toast]);

  const updateCompanyInfo = async (info: CompanyInfo) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data: existingData, error: checkError } = await supabase
        .from('company_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingData) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('company_settings')
          .update({
            company_name: info.companyName,
            contact_person: info.contactPerson,
            email: info.email,
            phone: info.phone || null,
            website: info.website || null,
            address: info.address,
            logo_url: info.logoUrl || null,
            finma_nr: info.finmaNr || null,
            primary_color: info.brandColors?.primary || '#18B08D',
            secondary_color: info.brandColors?.secondary || '#1A1F2C',
            accent_color: info.brandColors?.accent || '#7E69AB',
            facebook_url: info.socialMedia?.facebook || null,
            instagram_url: info.socialMedia?.instagram || null,
            linkedin_url: info.socialMedia?.linkedin || null,
            youtube_url: info.socialMedia?.youtube || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingData.id);

        if (updateError) throw updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('company_settings')
          .insert({
            user_id: user.id,
            company_name: info.companyName,
            contact_person: info.contactPerson,
            email: info.email,
            phone: info.phone || null,
            website: info.website || null,
            address: info.address,
            logo_url: info.logoUrl || null,
            finma_nr: info.finmaNr || null,
            primary_color: info.brandColors?.primary || '#18B08D',
            secondary_color: info.brandColors?.secondary || '#1A1F2C',
            accent_color: info.brandColors?.accent || '#7E69AB',
            facebook_url: info.socialMedia?.facebook || null,
            instagram_url: info.socialMedia?.instagram || null,
            linkedin_url: info.socialMedia?.linkedin || null,
            youtube_url: info.socialMedia?.youtube || null,
          });

        if (insertError) throw insertError;
      }

      setCompanyInfo(info);
      localStorage.setItem('companyInfo', JSON.stringify(info));

      toast({
        title: 'Erfolgreich gespeichert',
        description: 'Die Unternehmensdaten wurden erfolgreich aktualisiert.',
      });
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast({
        title: 'Fehler',
        description: 'Fehler beim Speichern der Unternehmensdaten.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CompanySettingsContext.Provider
      value={{
        companyInfo,
        updateCompanyInfo,
        isModalOpen,
        setIsModalOpen,
        isLoading,
      }}
    >
      {children}
    </CompanySettingsContext.Provider>
  );
};

export const useCompanySettings = () => useContext(CompanySettingsContext);
